/**
 * =============================================================================
 * SCRIPT-NAME:   AdsPerfDashboard.js
 * PROJEKT:       Freiseinapp - Admin-Zentrale
 * BESCHREIBUNG:  Performance-Dashboard fuer Google Ads Kampagnen.
 *                Zeigt KPIs aus 6 Bereichen fuer einen waehlbaren Zeitraum.
 *
 * VERSION:       v1.1
 * DATUM:         2026-03-03
 *
 * AENDERUNGEN v1.1:
 * - Authorization Bearer Token in beiden fetch-Aufrufen ergaenzt.
 * =============================================================================
 */

import React, { useState, useEffect } from 'react';
import BackButton from "./BackButton";
import "./AdsPerfDashboard.css";

const BEREICHE = [
    { key: 'campaigns', label: 'Kampagnen-Uebersicht' },
    { key: 'devices',   label: 'Geraete' },
    { key: 'age',       label: 'Altersgruppen' },
    { key: 'schedule',  label: 'Werbezeitplan' },
    { key: 'keywords',  label: 'Keywords' },
    { key: 'assets',    label: 'Assets' },
];

const API = '/api/ads_perf_report.php';

function getDefaultDates() {
    const today = new Date();
    const bis = new Date(today);
    bis.setDate(bis.getDate() - 1);
    const von = new Date(today);
    von.setDate(von.getDate() - 7);
    const fmt = d => d.toISOString().split('T')[0];
    return { von: fmt(von), bis: fmt(bis) };
}

export default function AdsPerfDashboard() {
    const defaults = getDefaultDates();
    const [von, setVon]               = useState(defaults.von);
    const [bis, setBis]               = useState(defaults.bis);
    const [campaigns, setCampaigns]   = useState([]);
    const [selected, setSelected]     = useState([]);
    const [results, setResults]       = useState({});
    const [loading, setLoading]       = useState({});
    const [errors, setErrors]         = useState({});

    // Kampagnen dynamisch laden
    useEffect(() => {
        const token = localStorage.getItem('token') || '';
        fetch('/api/ads_perf_campaigns_list.php', {
            headers: { 'Authorization': 'Bearer ' + token }
        })
            .then(r => r.json())
            .then(data => {
                setCampaigns(data);
                setSelected(data.map(c => String(c.campaign_id)));
            })
            .catch(() => setCampaigns([]));
    }, []);

    function toggleCampaign(id) {
        const sid = String(id);
        setSelected(prev =>
            prev.includes(sid) ? prev.filter(x => x !== sid) : [...prev, sid]
        );
    }

    function toggleAll() {
        if (selected.length === campaigns.length) {
            setSelected([]);
        } else {
            setSelected(campaigns.map(c => String(c.campaign_id)));
        }
    }

    async function loadBereich(key) {
        if (!selected.length) return;
        setLoading(prev => ({ ...prev, [key]: true }));
        setErrors(prev => ({ ...prev, [key]: null }));
        try {
            const token = localStorage.getItem('token') || '';
            const params = new URLSearchParams({
                von,
                bis,
                campaign_ids: selected.join(','),
                bereich: key
            });
            const res  = await fetch(`${API}?${params}`, {
                headers: { 'Authorization': 'Bearer ' + token }
            });
            const data = await res.json();
            if (!data.success) throw new Error(data.message || 'API Fehler');
            setResults(prev => ({ ...prev, [key]: data.data }));
        } catch (e) {
            setErrors(prev => ({ ...prev, [key]: e.message || 'Fehler beim Laden.' }));
        } finally {
            setLoading(prev => ({ ...prev, [key]: false }));
        }
    }

    function renderTable(data) {
        if (!data || !data.length) return <p className="perf-empty">Keine Daten.</p>;
        const cols = Object.keys(data[0]);
        return (
            <div className="perf-table-wrap">
                <table className="perf-table">
                    <thead>
                        <tr>{cols.map(c => <th key={c}>{c}</th>)}</tr>
                    </thead>
                    <tbody>
                        {data.map((row, i) => (
                            <tr key={i}>
                                {cols.map(c => <td key={c}>{row[c] ?? '---'}</td>)}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    return (
        <div className="perf-page">
            <div className="perf-box">

                <header className="perf-header">
                    <h2>Ads Performance Dashboard</h2>
                </header>

                <section className="perf-filter">
                    <div className="perf-filter-row">
                        <label>Von</label>
                        <input type="date" value={von} onChange={e => setVon(e.target.value)} />
                        <label>Bis</label>
                        <input type="date" value={bis} onChange={e => setBis(e.target.value)} />
                    </div>
                    <div className="perf-campaigns">
                        <div className="perf-campaigns-header">
                            <span className="perf-filter-label">Kampagnen</span>
                            <button className="perf-btn-small" onClick={toggleAll}>
                                {selected.length === campaigns.length ? 'Alle ab' : 'Alle an'}
                            </button>
                        </div>
                        <div className="perf-campaign-list">
                            {campaigns.map(c => (
                                <label key={c.campaign_id} className="perf-campaign-item">
                                    <input
                                        type="checkbox"
                                        checked={selected.includes(String(c.campaign_id))}
                                        onChange={() => toggleCampaign(c.campaign_id)}
                                    />
                                    {c.campaign_name}
                                </label>
                            ))}
                        </div>
                    </div>
                </section>

                {BEREICHE.map(({ key, label }) => (
                    <section key={key} className="perf-section">
                        <div className="perf-section-header">
                            <h3>{label}</h3>
                            <button
                                className="perf-btn"
                                onClick={() => loadBereich(key)}
                                disabled={loading[key] || !selected.length}
                            >
                                {loading[key] ? 'Laedt...' : 'Laden'}
                            </button>
                        </div>
                        {errors[key] && <p className="perf-error">{errors[key]}</p>}
                        {results[key] && renderTable(results[key])}
                    </section>
                ))}

                <div className="perf-footer">
                    <BackButton />
                </div>

            </div>
        </div>
    );
}